// src/auth/userBoundary.js
//
// Central user-boundary mechanism: fires registered reset callbacks
// whenever the active user's DB-backed id changes from one non-null
// value to a different non-null value (real user-switch within the
// same tab — impersonation, hot-swap of identity for testing).
//
// Why a central mechanism: many Zustand stores hold user-scoped data
// (cases, runs, layers, AOIs, project selection, etc.). Without a
// shared reset trigger, each one risks leaking previous-user data
// into the next user's view when the tab survives the switch — the
// known classes are:
//   - useProjectStore.activeProjectId carrying into a different user's
//     /tile/session request (caught earlier when the demo operator's
//     session inherited a Dreamhammer project_id)
//   - useAnalyticsProductsStore caching the previous user's product
//     codes, surfacing in TopNav's mode list
//   - useQcStore.casesList carrying the previous user's case visibility
//     (identified during Day 9 Day 2 followup)
//
// Logout-then-login goes through the KC redirect → full page reload
// path, which boots all stores fresh — so we DON'T fire on null
// transitions. We only catch the in-tab switch.

import { useAuthStore } from "../stores/useAuthStore";

const resetCallbacks = new Set();
let lastUserId = null;
let initialized = false;

/**
 * Register user-switch callbacks. Returns an unsubscribe function. Stores call
 * this at module-init time:
 *
 *   registerUserBoundaryReset(
 *     () => useQcStore.getState()._clearUserScoped(),   // clear (required)
 *     () => useQcStore.getState().listCases(),          // refetch (optional)
 *   );
 *
 * On an in-tab user switch we run every `clear` first, then every `refetch`.
 * The refetch is what repopulates a cleared store for the incoming user —
 * without it, a cleared store stays empty until a component re-mounts (page
 * refresh). Pass refetch only for stores that load global/user-scoped data on
 * demand; omit it for session-only state.
 *
 * Side-effect-free at module load (only populates a Set) — the auth
 * subscription is wired separately by initUserBoundary().
 */
export function registerUserBoundaryReset(clear, refetch) {
  const entry = { clear, refetch };
  resetCallbacks.add(entry);
  return () => resetCallbacks.delete(entry);
}

/**
 * Wire the auth-store subscription. Call ONCE at app startup (App mount).
 *
 * WHY a runtime init instead of subscribing at module load: userBoundary sits
 * in an import cycle — the wired stores (useProjectStore / useQcStore /
 * useAnalyticsProductsStore) import this module at load, and the auth store's
 * own chain (useAuthStore → fetchWithAuth → useProjectStore) closes the loop
 * back. If this module called `useAuthStore.subscribe(...)` at eval time, it
 * could run mid-cycle while useAuthStore's export is still undefined →
 * "Cannot read properties of undefined (reading 'subscribe')" (broke vitest
 * suites that import any wired store). Deferring the subscription to a runtime
 * call — after the whole module graph has settled — sidesteps the cycle and
 * keeps module load free of side effects (so tests that don't exercise
 * user-boundary behavior never touch it). Idempotent.
 */
export function initUserBoundary() {
  if (initialized) return;
  initialized = true;
  useAuthStore.subscribe((state) => {
    const id = state.profile?.id ?? null;
    if (id === null) {
      // Logged-out / pre-profile-load. Reset our tracking but don't
      // fire callbacks — KC-redirect logout already handles fresh boot.
      lastUserId = null;
      return;
    }
    if (lastUserId !== null && lastUserId !== id) {
      // Real switch from one user to another inside the same tab (impersonation,
      // identity hot-swap). Clear every user-scoped store FIRST, then refetch in
      // a second pass — so a refetch never reads another store mid-clear, and
      // every store is repopulated for the incoming user. Without the refetch
      // pass, cleared stores stayed empty until a page refresh (e.g. TopNav's
      // mode tabs went blank after a restricted→non-restricted switch).
      for (const { clear } of resetCallbacks) {
        try {
          clear?.();
        } catch (err) {
          console.error("[userBoundary] clear callback failed", err);
        }
      }
      for (const { refetch } of resetCallbacks) {
        try {
          refetch?.();
        } catch (err) {
          console.error("[userBoundary] refetch callback failed", err);
        }
      }
    }
    lastUserId = id;
  });
}
